import {TreeDataNode} from "antd";
import { IMG_NOT_FOUND } from '@tools/utils/imageUtils.tsx';
import { ReactElement } from 'react';
import { ImageInfo } from '@app-types/commons/images.types.ts';
export interface ExtendedTreeDataNode extends TreeDataNode {
  depth?: number;
  isRootNode?: boolean;
  hasChildren: boolean;
  description: string;
  img_url: string;
}
/**
 * Convierte categorías al formato de `Tree` y conserva si el nodo pertenece al
 * primer nivel real del árbol para diferenciarlo de hijos y agrupadores visuales.
 *
 * @param categories Categorías del dominio a transformar.
 * @param isRootLevel Indica si las categorías recibidas están en el nivel raíz.
 * @returns Nodos extendidos listos para renderizar en los árboles del e-shop.
 */
export const transformCategoriesToTreeData = (
  categories: Category[],
  isRootLevel = true,
): ExtendedTreeDataNode[] => {
  return categories.map((category) => ({
    key: category.id.toString(),
    title: category.name,
    description: category.description ?? '',
    children: category.children
      ? transformCategoriesToTreeData(category.children, false)
      : [],
    depth: category.depth,
    isRootNode: isRootLevel,
    hasChildren:
      category.has_children ??
      Boolean(category.children && category.children.length > 0),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    img_url: category.image?.thumbnails?.default ?? IMG_NOT_FOUND,
  }));
};

const normalizeRootName = (value: string): string =>
  value.trim().toLocaleLowerCase();

export const groupDuplicateRootNames = (
  nodes: ExtendedTreeDataNode[],
): ExtendedTreeDataNode[] => {
  const groups = new Map<string, ExtendedTreeDataNode[]>();
  const firstSeenOrder: string[] = [];

  for (const node of nodes) {
    const title = typeof node.title === 'string' ? node.title : String(node.title ?? '');
    const bucketKey = normalizeRootName(title);
    if (!groups.has(bucketKey)) {
      groups.set(bucketKey, []);
      firstSeenOrder.push(bucketKey);
    }
    groups.get(bucketKey)!.push(node);
  }

  const groupedRoots: ExtendedTreeDataNode[] = [];
  for (const key of firstSeenOrder) {
    const group = groups.get(key) ?? [];
    if (group.length <= 1) {
      if (group[0]) groupedRoots.push(group[0]);
      continue;
    }

    const first = group[0];
    const title = typeof first.title === 'string' ? first.title : String(first.title ?? '');

    groupedRoots.push({
      key: `group-root:${key}`,
      title,
      description: `Agrupación visual de ${group.length} raíces con el mismo nombre`,
      depth: 0,
      isRootNode: false,
      hasChildren: true,
      img_url: IMG_NOT_FOUND,
      isLeaf: false,
      selectable: false,
      children: group,
    });
  }

  return groupedRoots;
};

export interface Category {
  id: number;
  slug: string;
  name: string;
  description?: string;
  image?: ImageInfo;
  sort_order: number;
  parent_id?: number | null;
  depth?: number;
  path?: string;
  children?: Category[];
  has_children?: boolean;
}

export interface CategoryLabel {
  id: number;
  name: string;
}

export interface CategoryCreate {
  name: string;
  slug: string;
  description?: string;
  image?: ImageInfo;
  sort_order?: number;
  parent_id?: number | null;
  after_category_id?: number | null;
}

export interface CartItemDisplay {
    key: string;
    title: string;
    quantity: number;
    price: string;
    currency: string;
    imageUrl?: string;
    description: ReactElement;
    imageElement: ReactElement;
}
